<script lang="ts">
    import type {YearMonth} from 'shared/components/kp-calendar/types';
    import {onMount} from 'svelte';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {getCurrentLanguage} from 'core/svelte-context/context';
    import {uuid} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {createEventDispatcher} from 'svelte';
    import KpPopoverPanel from 'shared/ui-widgets/popover/KpPopoverPanel.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import LabeledValue from 'src/features/erp-sutor/pages/project-detail/reports-optimisation/overviews/components/LabeledValue.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';

    export let selectedMonth: YearMonth | null = null;
    export let yearOffset = 0;

    const dispatch = createEventDispatcher<{'month-selected': YearMonth}>();
    const locale = getCurrentLanguage();
    const popoverId = uuid();
    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-end'));

    let currentViewYear: number;
    let navigateDirection = 0;
    let allMonths: {name: string, value: YearMonth}[] = [];

    onMount(() => {
        const now = new Date();
        const currentYear = now.getFullYear() + yearOffset;
        currentViewYear = currentYear;

        if (!selectedMonth) {
            selectedMonth = `${currentYear}-${String(now.getMonth() + 1).padStart(2, '0')}` as YearMonth;
            dispatch('month-selected', selectedMonth);
        }

        updateMonths(currentViewYear);
    });

    function updateMonths(year: number) {
        allMonths = Array.from({length: 12}, (_, i) => {
            const monthValue = String(i + 1).padStart(2, '0');
            return {
                name: new Date(year, i).toLocaleDateString(locale, {month: 'long'}),
                value: `${year}-${monthValue}` as YearMonth
            };
        });
    }

    function formatMonth(value: string): string {
        if (!value) return 'No month selected';
        const [year, month] = value.split('-');
        const monthNameString = new Date(Number(year), Number(month) - 1).toLocaleDateString(locale, {
            month: 'long',
            year: 'numeric'
        });

        return `${monthNameString.charAt(0).toUpperCase()}${monthNameString.substring(1)}`;
    }

    function handleMonthSelect(monthValue: YearMonth) {
        selectedMonth = monthValue;

        dispatch('month-selected', monthValue);
    }

    function navigateYear(offset: number) {
        currentViewYear += offset;
        navigateDirection = offset < 0 ? -1 : (offset > 0 ? 1 : 0);
        updateMonths(currentViewYear);
    }

    const handlePopoverClose = () => {
        navigateDirection = 0;
        updateMonths(currentViewYear);
    };
</script>

<div class="month-picker" use:floatingRef>
    <LabeledValue label="Vybraný měsíc">
        <span class="month-text">{formatMonth(selectedMonth)}</span>
    </LabeledValue>

    <KpButton use={[floatingRef]} popovertarget="{popoverId}">
        <IconedContent icon="calendar">
            Vybrat jiný měsíc
        </IconedContent>
    </KpButton>

    <KpPopoverPanel id="{popoverId}"
                    use={[floatingContent]}
                    additionalClasses="datetime-picker-popover"
                    on:popover-close={handlePopoverClose}
                    let:togglePopover>

        <div class="month-selector-container" in:fly={{duration: 200, y: 5}}>
            <div class="month-selector-header">
                <Flex alignItems="center" gap="m">
                    <KpIconButton buttonSize="xs" icon="arrow-small-left" on:click={() => navigateYear(-1)}/>
                    <span class="year-display">{currentViewYear}</span>
                    <KpIconButton buttonSize="xs" icon="arrow-small-right" on:click={() => navigateYear(1)}/>
                </Flex>
            </div>

            {#key allMonths}
                <div class="month-selector-grid" in:fly={{duration: 250, x: 10 * navigateDirection}}>
                    {#each allMonths as month}
                        <button
                            type="button"
                            class="month-grid-item"
                            class:selected={selectedMonth === month.value}
                            on:click={() => {
                            handleMonthSelect(month.value);
                            togglePopover();
                        }}>

                            {month.name}
                        </button>
                    {/each}
                </div>
            {/key}
        </div>
    </KpPopoverPanel>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .month-picker {
        display: flex;
        align-items: center;
        gap: @spacing-ml;
        width: max-content;

        .month-text {
            font-size: @font-size-xl;
            font-weight: 500;
            line-height: 1;
            margin: 0;
        }
    }

    .month-selector-container {
        width: 320px;
        padding: @spacing-m;
    }

    .month-selector-header {
        margin-bottom: @spacing-m;

        .year-display {
            font-size: 18px;
            font-weight: 600;
            min-width: 80px;
            text-align: center;
        }
    }

    .month-selector-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-gap: @spacing-s;

        .month-grid-item {
            height: 48px;
            padding: @spacing-s;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: @border-radius-default;
            border: 1px solid @themed-border-default;
            background-color: @themed-body-bg;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: @font-size-sm;

            &:hover {
                background-color: @themed-body-bg-blue-highlighted;
            }

            &.selected {
                background-color: var(--brand-orange-new);
                color: white;
                border-color: var(--brand-orange-new);
            }
        }
    }
</style>